import { useState } from "react";
import { Collapse } from "../../../../../components/motion";
import { useIsDesktop } from "../../../../../hooks";
import { cn } from "../../../../../utils/classnames";
import { ExpandToggleSign } from "./ExpandToggleSign";
import { RichText } from "./RichText";

/**
 * Fine print that sits beside the hero on desktop and folds away on mobile.
 *
 * There is no room for a wall of text on a phone, so below `lg` the guest taps
 * the title to open it — and it slides open — while from `lg` up it is always
 * visible and the toggle is hidden.
 *
 * Desktop is the one case where JavaScript has to know the screen width: the
 * body has to count as open so the sliding panel never hides it.
 *
 * `bodyKey` is a translation key; its copy may use <p> and <strong>.
 */
export const AsideNote = ({ title, bodyKey, className }) => {
  const [isOpenOnMobile, setIsOpenOnMobile] = useState(false);
  const isDesktop = useIsDesktop();

  const isBodyVisible = isDesktop || isOpenOnMobile;

  return (
    <div
      className={cn(
        "tw:rounded-[8px] tw:bg-[#F9F9F9] tw:px-5 tw:py-4 tw:lg:p-8",
        className
      )}
    >
      <button
        type="button"
        onClick={() => setIsOpenOnMobile((wasOpen) => !wasOpen)}
        aria-expanded={isOpenOnMobile}
        className="tw:w-full tw:bg-transparent tw:border-0 tw:p-0 tw:flex tw:items-center tw:justify-between tw:gap-x-4 tw:text-left tw:cursor-pointer tw:lg:cursor-default"
      >
        <span className="tw:font-hanken-grotesk tw:font-bold tw:text-[15px] tw:leading-[22px] tw:text-[#282828]">
          {title}
        </span>
        <span className="tw:lg:hidden">
          <ExpandToggleSign isOpen={isOpenOnMobile} />
        </span>
      </button>

      <Collapse isOpen={isBodyVisible}>
        <div className="tw:h-px tw:bg-[#F1F1F1] tw:my-4 tw:lg:mt-[23px] tw:lg:mb-6" />
        {/* The design leaves one blank line between the two paragraphs. */}
        <RichText
          i18nKey={bodyKey}
          paragraphGapClass="tw:[&_p+p]:mt-[21px]!"
          className="tw:font-hanken-grotesk tw:text-[#4E4E4E] tw:text-[13px] tw:leading-[19px]"
        />
      </Collapse>
    </div>
  );
};
